import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { API_BASE } from '../../constants/general.constants';
import { isDappaErrorReportedInline } from '../../utils/dappa-request-url';
import {
  AddDappaApiParams,
  AddDappaEndpointParams,
  DappaApi,
  DappaEndpoint,
  UpdateDappaApiParams,
  UpdateDappaEndpointParams,
} from '../../interfaces/dappa.interface';

/**
 * Dappa API and endpoint authoring (EHQ-339).
 *
 * Every route is version-scoped: `/project/{projectId}/v/{versionId}/api...`.
 * Writes need the project lock on an editable HEAD version, exactly as workflow
 * edits do. The server answers 404 for every route when scripting is disabled
 * on the deployment, so callers gate on `DeploymentInfoService.scriptingEnabled`.
 *
 * Promise-based: `firstValueFrom` bridges HttpClient here, at the data
 * boundary, so stores and components stay free of RxJS.
 */
@Injectable({ providedIn: 'root' })
export class DappaApiService {
  readonly #http = inject(HttpClient);

  #apis(projectId: number, versionId: number): string {
    return `${API_BASE}/project/${projectId}/v/${versionId}/api`;
  }

  #endpoints(projectId: number, versionId: number, apiId: number): string {
    return `${this.#apis(projectId, versionId)}/${apiId}/endpoints`;
  }

  listApis(projectId: number, versionId: number): Promise<DappaApi[]> {
    return firstValueFrom(this.#http.get<DappaApi[]>(this.#apis(projectId, versionId)));
  }

  getApi(projectId: number, versionId: number, apiId: number): Promise<DappaApi> {
    return firstValueFrom(this.#http.get<DappaApi>(`${this.#apis(projectId, versionId)}/${apiId}`));
  }

  createApi(projectId: number, versionId: number, params: AddDappaApiParams): Promise<DappaApi> {
    return firstValueFrom(this.#http.post<DappaApi>(this.#apis(projectId, versionId), params));
  }

  updateApi(
    projectId: number,
    versionId: number,
    apiId: number,
    params: UpdateDappaApiParams,
  ): Promise<DappaApi> {
    return firstValueFrom(
      this.#http.put<DappaApi>(`${this.#apis(projectId, versionId)}/${apiId}`, params),
    );
  }

  async deleteApi(projectId: number, versionId: number, apiId: number): Promise<void> {
    await firstValueFrom(this.#http.delete(`${this.#apis(projectId, versionId)}/${apiId}`));
  }

  listEndpoints(projectId: number, versionId: number, apiId: number): Promise<DappaEndpoint[]> {
    return firstValueFrom(
      this.#http.get<DappaEndpoint[]>(this.#endpoints(projectId, versionId, apiId)),
    );
  }

  createEndpoint(
    projectId: number,
    versionId: number,
    apiId: number,
    params: AddDappaEndpointParams,
  ): Promise<DappaEndpoint> {
    return firstValueFrom(
      this.#http.post<DappaEndpoint>(this.#endpoints(projectId, versionId, apiId), params),
    );
  }

  updateEndpoint(
    projectId: number,
    versionId: number,
    apiId: number,
    endpointId: number,
    params: UpdateDappaEndpointParams,
  ): Promise<DappaEndpoint> {
    return firstValueFrom(
      this.#http.put<DappaEndpoint>(
        `${this.#endpoints(projectId, versionId, apiId)}/${endpointId}`,
        params,
      ),
    );
  }

  async deleteEndpoint(
    projectId: number,
    versionId: number,
    apiId: number,
    endpointId: number,
  ): Promise<void> {
    await firstValueFrom(
      this.#http.delete(`${this.#endpoints(projectId, versionId, apiId)}/${endpointId}`),
    );
  }
}

/**
 * The message to show for a failed Dappa request: the server's unified
 * `{ code, message }` envelope when present, otherwise the HTTP error's own text.
 *
 * A duplicate API name, endpoint name or method+path is a 409 `CONFLICT` whose
 * message already says what collided ("An endpoint for GET /orders already
 * exists in this API"), so it is shown as-is like any other server message.
 */
export function dappaErrorMessage(error: unknown): string {
  const e = error as
    | { error?: { code?: unknown; message?: unknown }; message?: unknown }
    | null
    | undefined;
  const serverMessage = e?.error?.message;
  if (typeof serverMessage === 'string' && serverMessage.trim()) {
    return serverMessage;
  }
  if (typeof e?.message === 'string' && e.message.trim()) {
    return e.message;
  }
  return 'The request failed.';
}

/**
 * The message a Dappa dialog or page should show for a failed request, or
 * `null` when the error interceptor has already reported it (401, 403, 5xx and
 * network failures). Without this a 403 "project not locked" would appear twice:
 * once as the global "lock the project" warning and again inline. An error with
 * no HTTP status never reached the interceptor, so it is always shown here.
 */
export function dappaInlineErrorMessage(error: unknown): string | null {
  const status = (error as { status?: unknown } | null | undefined)?.status;
  if (typeof status === 'number' && !isDappaErrorReportedInline(status)) {
    return null;
  }
  return dappaErrorMessage(error);
}
